import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  User,
  Home,
  CalendarDays,
  LogOut,
  Stethoscope,
  Menu,
  X,
} from 'lucide-react';

const patientNavItems = [
  { to: '/mi-perfil', icon: Home, label: 'Inicio', end: true },
  { to: '/mis-citas', icon: CalendarDays, label: 'Mis citas', end: false },
  { to: '/mi-cuenta', icon: User, label: 'Mi cuenta', end: false },
];

export const PatientSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (window.innerWidth < 768) onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`
          w-64 h-screen bg-white border-r border-slate-100 flex flex-col fixed left-0 top-0 z-50
          transform transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <div className="flex items-center justify-between p-6 md:hidden border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#335C67] rounded-xl flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-[#2B2D42] font-['Manrope']">Consulterest</h1>
              <p className="text-xs text-[#8D99AE]">Mi espacio</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="md:hidden" aria-label="Cerrar menú">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="hidden md:flex p-6 items-center gap-3">
          <div className="w-10 h-10 bg-[#335C67] rounded-xl flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-[#2B2D42] font-['Manrope']">Consulterest</h1>
            <p className="text-xs text-[#8D99AE]">Mi espacio</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {patientNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-[#335C67] text-white shadow-sm'
                    : 'text-[#5D5C61] hover:bg-[#F4F1EA] hover:text-[#2B2D42]'
                }`
              }
              data-testid={`patient-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#335C67] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#2B2D42] truncate">{user?.name}</p>
              <p className="text-xs text-[#8D99AE]">Paciente</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-[#9E2A2B] hover:text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
            onClick={handleLogout}
            data-testid="patient-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </Button>
        </div>
      </aside>
    </>
  );
};

export const PatientLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <PatientSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <button
        onClick={() => setSidebarOpen(true)}
        className="fixed top-4 left-4 z-30 md:hidden p-2 bg-white rounded-lg shadow-md border border-slate-200 hover:bg-[#F4F1EA] transition-colors"
        aria-label="Abrir menú"
        data-testid="patient-mobile-menu-btn"
      >
        <Menu className="w-6 h-6 text-[#2B2D42]" />
      </button>
      <main className="md:ml-64 min-h-screen pt-20 md:pt-0 flex flex-col">
        <div className="flex-1 p-4 md:p-6 max-w-4xl mx-auto w-full">
          {children}
        </div>
        <footer className="mt-auto py-4 px-6 border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-[#8D99AE]">
              Desarrollado por{' '}
              <a
                href="https://agsoftweb.com.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
              >
                Agsoftweb
              </a>
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};
